import { Mode } from '../../common/types';
import { BrowserTracker, newTracker } from '@snowplow/browser-tracker';
import { snowplowAnalyticsProperties } from '../environment';
import { isClient } from '../../common/utils';
import {
  endMediaTracking,
  SnowplowMediaPlugin,
  trackMediaEnd,
  trackMediaSelfDescribingEvent,
} from '@snowplow/browser-plugin-media';
import { HbbTVPlayer } from '../Player/adapters/HbbTVPlayer';
import { Html5Player } from '../Player/adapters/HTML5Player';
import {
  AnalyticsContext,
  ContextContentLanguage,
  mediaTrackingGenerator,
} from './snowplowContexts';
import { Language } from '@yleisradio/areena-types';
import { AudioItem, SubtitleItem } from '../appTypings';

let tracker: BrowserTracker | null | undefined;

export function getTracker(mode: Mode) {
  if (!isClient()) {
    return {
      tracker: null,
      isSnowplowRunning: false,
    };
  }

  const { getAppId, collectorUrl, trackerNamespace } =
    snowplowAnalyticsProperties;

  const appId = getAppId(modeToManufacturer(mode));

  if (!tracker) {
    tracker = newTracker(trackerNamespace, collectorUrl, {
      appId,
      appVersion: window.yleLeanback.VERSION,
      discoverRootDomain: true,
      cookieSameSite: 'Lax',
      contexts: {
        browser: true,
        session: true,
      },
      platform: 'tv',
      plugins: [SnowplowMediaPlugin()],
    });
  }

  return {
    tracker,
    isSnowplowRunning: !!tracker,
  };
}

function modeToManufacturer(mode: Mode) {
  switch (mode) {
    case 'tizen':
      return 'samsung';
    case 'webos':
      return 'lg';
    default:
      return mode;
  }
}

enum MediaType {
  Audio = 'audio',
  Video = 'video',
}

export type getMediaTrackingConfigurationProps = {
  mediaSessionId: string;
  player: HbbTVPlayer | Html5Player | undefined;
  isLiveMedia: boolean;
  isAudio: boolean;
  isPaused: boolean;
  label: string;
  programId: string;
  dvr_window: string | undefined;
  publication_id: string | undefined;
  section: string | undefined;
  content_language: ContextContentLanguage;
  service_language: Language;
  service_id: string | undefined;
  media_id: string;
};

export type MediaTrackingConfiguration = {
  id: string;
  player: {
    currentTime: number;
    duration: number;
    livestream: boolean;
    label: string;
    mediaType: MediaType;
    paused: boolean;
  };
  context: AnalyticsContext[];
};

export const getMediaTrackingConfiguration = ({
  programId,
  mediaSessionId,
  player,
  dvr_window,
  isLiveMedia,
  isAudio,
  isPaused,
  label,
  publication_id,
  section,
  content_language,
  service_language,
  service_id,
  media_id,
}: getMediaTrackingConfigurationProps): MediaTrackingConfiguration => {
  const config = {
    id: mediaSessionId,
    pings: { pingInterval: 30 },
    player: {
      currentTime: player?.getPosition() || 0,
      duration: player?.getDuration() || 0,
      livestream: isLiveMedia,
      label,
      mediaType: isAudio ? MediaType.Audio : MediaType.Video,
      paused: isPaused,
    },
    context: mediaTrackingGenerator({
      section,
      content_language,
      service_language,
      programId,
      isLiveMedia,
      dvr_window,
      service_id,
      media_id,
      publication_id,
    }),
  };

  return config;
};

export const onPlayerClose = (mediaSessionId: string) => {
  trackMediaSelfDescribingEvent({
    id: mediaSessionId,
    event: {
      schema:
        'iglu:com.snowplowanalytics.snowplow.media/ping_event/jsonschema/1-0-0',
      data: {},
    },
  });
  endMediaTracking({ id: mediaSessionId });
};

export const onMediaEnd = (mediaSessionId: string) => {
  trackMediaEnd({ id: mediaSessionId });
};

export const onSubtitleChange = (
  mediaSessionId: string,
  selectedSubtitle?: SubtitleItem
) =>
  trackMediaSelfDescribingEvent({
    id: mediaSessionId,
    event: {
      data: {
        kind: selectedSubtitle?.kind.toLowerCase() ?? null,
        language_code: selectedSubtitle?.language ?? null,
        type: selectedSubtitle?.type.toLocaleLowerCase() ?? null,
      },
      schema: 'iglu:fi.yle.player/caption_track/jsonschema/2-0-1',
    },
  });

export const onAudioChange = (
  mediaSessionId: string,
  selectedAudio?: AudioItem
) =>
  trackMediaSelfDescribingEvent({
    id: mediaSessionId,
    event: {
      data: {
        format: selectedAudio?.format ?? null,
        language_code: selectedAudio?.language ?? null,
        type: selectedAudio?.kind.toLowerCase() ?? null,
      },
      schema: 'iglu:fi.yle.player/audio_track/jsonschema/2-0-1',
    },
  });
